import axiosInstance from "./apiinterseptor";

export type ReportSummary = {
  subJobs: number;
  approaches: number;
  applications: number;
  scheduled: number;
  attended: number;
  interviewNotAttended: number;
  selections: number;
  joins: number;
  notJoined: number;
  verified: number;
  notVerified: number;
  pending: number;
  approved: number;
  notApproved: number;
  approvalPending: number;
  tenureClaimed: number;
  onboardedHm: number;
  onboardedHr: number;
  convertedHrToHm: number;
  activeHm: number;
  activeHr: number;
  potentialEarning: number;
  receivedEarning: number;
  payableEarning: number;
  claimedEarning: number;
};

export type ReportEarningsBreakdownRow = {
  userId: string;
  username: string;
  role: string;
  potentialEarning: number;
  receivedEarning: number;
  payableEarning: number;
  claimedEarning: number;
  pinned?: boolean;
};

export type ReportPinnedHmItem = {
  userId: string;
  username: string;
  role: string;
};

export type ReportSummaryParams = {
  recruiterId?: string;
  role?: string;
  dateFrom?: string;
  dateTo?: string;
};

export type ReportDailySeriesPoint = ReportSummary & {
  day: string;
};

export type ReportListableMetric =
  | "approaches"
  | "applications"
  | "scheduled"
  | "attended"
  | "interviewNotAttended"
  | "selections"
  | "joins"
  | "notJoined"
  | "verified"
  | "notVerified"
  | "pending"
  | "approved"
  | "notApproved"
  | "approvalPending"
  | "tenureClaimed";

export type ReportApproachRecord = {
  id: string;
  name: string;
  phone: string;
  email: string;
  city?: string;
  source?: string;
  callStatus?: string;
  createdAt: string;
  sourcedBy?: string;
  led?: string;
  shm?: string;
};

export type ReportApplicationRecord = {
  applicationId: string;
  candidateName: string;
  candidatePhone: string;
  candidateEmail: string;
  jobTitle: string;
  companyName?: string;
  location?: string;
  currentStatus: string;
  appliedOn?: string;
  selectedDate?: string;
  joiningDate?: string;
  verificationStatus?: string;
  sourcedBy?: string;
  led?: string;
  shm?: string;
  lastWorkingDay?: string;
};

export type ReportMetricRecordsParams = ReportSummaryParams & {
  page?: number;
  limit?: number;
  search?: string;
  export?: boolean;
};

export type ReportMetricRecordsResponse = {
  metric: ReportListableMetric;
  source: "approaches" | "applications";
  page: number;
  limit: number;
  totalResults: number;
  totalPages: number;
  records: ReportApproachRecord[] | ReportApplicationRecord[];
};

const buildReportQueryParams = (params: ReportSummaryParams = {}) => {
  const queryParams = new URLSearchParams();

  if (params.recruiterId && params.recruiterId !== "all") {
    queryParams.set("recruiterId", params.recruiterId);
  }
  if (params.role && params.role !== "all") {
    queryParams.set("role", params.role);
  }
  if (params.dateFrom) {
    queryParams.set("dateFrom", params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.set("dateTo", params.dateTo);
  }

  return queryParams;
};

export const getReportSummary = async (params: ReportSummaryParams = {}) => {
  const queryParams = buildReportQueryParams(params);
  const queryString = queryParams.toString();
  const response = await axiosInstance.get(
    `/report/summary${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
};

export const getReportDailySeries = async (params: ReportSummaryParams = {}) => {
  const queryParams = buildReportQueryParams(params);
  const queryString = queryParams.toString();
  const response = await axiosInstance.get(
    `/report/daily-series${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
};

export const getReportEarningsBreakdown = async (
  params: Pick<ReportSummaryParams, "dateFrom" | "dateTo"> = {}
) => {
  const queryParams = new URLSearchParams();
  if (params.dateFrom) queryParams.set("dateFrom", params.dateFrom);
  if (params.dateTo) queryParams.set("dateTo", params.dateTo);
  const queryString = queryParams.toString();
  const response = await axiosInstance.get(
    `/report/earnings-breakdown${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
};

export const addReportPinnedHm = async (userId: string) => {
  const response = await axiosInstance.post(`/report/earnings-pinned-hms`, {
    userId,
  });
  return response.data;
};

export const removeReportPinnedHm = async (userId: string) => {
  const response = await axiosInstance.delete(
    `/report/earnings-pinned-hms/${userId}`
  );
  return response.data;
};

export const getReportMetricRecords = async (
  metric: ReportListableMetric,
  params: ReportMetricRecordsParams = {}
) => {
  const queryParams = buildReportQueryParams(params);

  if (params.page) {
    queryParams.set("page", String(params.page));
  }
  if (params.limit) {
    queryParams.set("limit", String(params.limit));
  }
  if (params.search?.trim()) {
    queryParams.set("search", params.search.trim());
  }
  if (params.export) {
    queryParams.set("export", "true");
  }

  const queryString = queryParams.toString();
  const response = await axiosInstance.get(
    `/report/metric/${metric}/records${queryString ? `?${queryString}` : ""}`
  );
  return response.data;
};
